import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { HandCoins, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { k: "full_name", label: "Full name", type: "text" },
  { k: "email", label: "Email", type: "email" },
  { k: "phone", label: "Mobile number", type: "tel", ph: "+63 917 ..." },
  { k: "address", label: "Address", type: "text" },
  { k: "password", label: "Password (min 8 chars)", type: "password" },
];

export default function Register() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", address: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/register", { ...form, address: form.address || null });
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check your details");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="portal-root borrower-grid min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-blue-100 p-7">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-2xl bg-[#00A8DE] flex items-center justify-center">
            <HandCoins className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-display font-extrabold text-xl text-slate-900 leading-none">LendingPH</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-bold mt-1">Borrower Portal</div>
          </div>
        </div>
        {done ? (
          <div className="text-center py-6" data-testid="register-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-3">Application received</h1>
            <p className="text-slate-500 text-sm mt-2">
              Your account is awaiting admin approval. You can sign in once it's approved.
            </p>
            <Link to="/login" className="inline-block mt-6 text-blue-600 font-semibold" data-testid="register-go-login">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <h1 className="font-display text-2xl font-bold text-slate-900">Create your account</h1>
            {FIELDS.map((f) => (
              <div key={f.k}>
                <Label className="text-xs text-slate-500">{f.label}</Label>
                <Input
                  type={f.type}
                  required={f.k !== "address"}
                  placeholder={f.ph}
                  value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  data-testid={`register-${f.k}-input`}
                  className="h-11 bg-slate-50 border-slate-200 text-slate-900"
                />
              </div>
            ))}
            <Button type="submit" disabled={busy} data-testid="register-submit-button"
              className="w-full h-11 bg-[#00A8DE] hover:bg-sky-600 text-white font-semibold rounded-xl">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Create account
            </Button>
            <p className="text-sm text-slate-500 text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-blue-600 font-semibold" data-testid="register-login-link">Sign in</Link>
            </p>
          </form>
        )}
      </div>
      <Toaster richColors position="top-center" />
    </div>
  );
}
